/**
 * `/strategy/:accountId` — the one-page account strategy, laid out after the
 * "Strategic Account Plan" slide: a header band, then the eight numbered
 * sections in a grid that collapses to one column at narrow widths.
 *
 * **One payload** (`GET /api/account-strategies/:accountId`) carries the
 * computed sections, the stored ones and the caller's membership. Edit controls
 * are shown only when `canWriteAccountStrategy` — the SAME shared predicate the
 * server enforces with — says yes; the server refuses the write regardless
 * (§2 rule 1: hiding is convenience, not control). The one exception, the
 * assigned owner of a next step, may change that step's status only.
 *
 * Sections 2, 4 and 5 and the header are computed from funnel data and are
 * never edited here.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Download, ImageOff, Loader2, Trash2, Upload } from 'lucide-react';
import {
  ACTION_STATUSES,
  STRATEGIC_STEP_HEALTH_LABELS,
  STRATEGY_BLOCKER_CATEGORIES,
  STRATEGY_BLOCKER_LABELS,
  STRATEGY_IMAGE_CONTENT_TYPES,
  STRATEGY_IMAGE_LABELS,
  STRATEGY_OBJECTIVE_RULE,
  STRATEGY_VALUE_FIELDS,
  STRATEGY_VALUE_RULE,
  canAssigneeUpdateStrategicStep,
  canWriteAccountStrategy,
  type AccountStrategyView,
  type StrategicStepHealth,
  type StrategyContactRef,
  type StrategyImageSlot,
  type StrategyUserRef,
} from '@menlo/shared';
import { useMe } from '@/auth/useMe';
import { api, ApiError, apiRaw } from '@/lib/api';
import { useApiMutation, useApiQuery } from '@/lib/queries';
import { useReferenceData } from '@/lib/reference';
import { useToast } from '@/lib/toast';
import { formatDate, formatDateTime, formatMoney, humanize } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AttachmentAttestation } from '@/features/export/ExportAttestation';
import { cn } from '@/lib/utils';

type View = AccountStrategyView;

export function StrategyPage(): React.ReactElement {
  const { accountId = '' } = useParams();
  const { data: me } = useMe();
  const key = ['account-strategy', accountId] as const;
  const query = useApiQuery<View>(key, `/api/account-strategies/${accountId}`);

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (query.error) {
    return (
      <div className="mx-auto max-w-xl space-y-2 rounded-md border p-6 text-sm">
        <p className="font-medium">
          {query.error.status === 403 ? 'You cannot see this strategy.' : query.error.message}
        </p>
        {query.error.status === 403 && (
          <p className="text-muted-foreground">
            Account strategies are visible to managers and to the account&rsquo;s team &mdash; its
            owner, and the owner or FAE of any of its opportunities.
          </p>
        )}
        <Link to="/strategy" className="text-xs underline">
          Back to strategies
        </Link>
      </div>
    );
  }
  const view = query.data;
  if (!view || !me) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const actor = { userId: me.user.id, tier: me.tier };
  const canWrite = canWriteAccountStrategy(actor, view.membership);

  return (
    <StrategyBody view={view} canWrite={canWrite} actorId={me.user.id} tier={me.tier} qkey={key} />
  );
}

// ---------------------------------------------------------------------------

interface BodyProps {
  view: View;
  canWrite: boolean;
  actorId: string;
  tier: 'User' | 'Manager' | 'Admin';
  qkey: readonly unknown[];
}

function StrategyBody({ view, canWrite, actorId, tier, qkey }: BodyProps): React.ReactElement {
  const base = `/api/account-strategies/${view.account.id}`;
  const invalidate = { invalidate: [qkey, ['account-strategies']] };
  const save = useApiMutation<Record<string, unknown>, Record<string, unknown>>(
    (body) => ({ path: base, method: 'PUT', body }),
    invalidate,
  );

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <Header view={view} canWrite={canWrite} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section n={1} title="Customer & relationship">
          <Relationship view={view} canWrite={canWrite} onSave={(b) => save.mutate(b)} />
        </Section>

        <Section n={2} title="Menlo coverage team" computed>
          <Field label="Sales lead">
            <UserName user={view.coverage.sales_lead} />
          </Field>
          <Field label="FAE">
            {view.coverage.faes.length === 0 ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <ul className="space-y-0.5">
                {view.coverage.faes.map((fae) => (
                  <li key={fae.id}>
                    <UserName user={fae} />
                  </li>
                ))}
              </ul>
            )}
          </Field>
        </Section>

        <Section n={3} title="Account objective">
          <OrderedList
            kind="objectives"
            base={base}
            canWrite={canWrite}
            invalidate={invalidate.invalidate}
            items={view.objectives.map((o) => ({ id: o.id, text: o.body }))}
          />
          <p className="mt-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {STRATEGY_OBJECTIVE_RULE}
          </p>
        </Section>

        <Section
          n={4}
          title="Customer system / end solution by project"
          computed
          className="lg:col-span-2"
        >
          <Projects view={view} />
          <ImageSlot
            slot="product"
            view={view}
            base={base}
            canWrite={canWrite}
            invalidate={invalidate.invalidate}
          />
        </Section>

        <Section n={5} title="Menlo content" computed>
          <Content view={view} />
        </Section>

        <Section n={6} title="Value we provide to customer" className="lg:col-span-2">
          <div className="space-y-3">
            {STRATEGY_VALUE_FIELDS.map((field) => (
              <Field key={field.column} label={field.label}>
                {canWrite ? (
                  <SaveOnBlurText
                    multiline
                    label={field.label}
                    value={view.value[field.column]}
                    placeholder={field.prompt}
                    onSave={(value) => save.mutate({ [field.column]: value })}
                  />
                ) : (
                  <Prose value={view.value[field.column]} />
                )}
              </Field>
            ))}
          </div>
          <p className="mt-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {STRATEGY_VALUE_RULE}
          </p>
        </Section>

        <Section n={8} title="Design-win / risk / competition">
          <Blockers
            view={view}
            base={base}
            canWrite={canWrite}
            invalidate={invalidate.invalidate}
          />
          <ImageSlot
            slot="competitor"
            view={view}
            base={base}
            canWrite={canWrite}
            invalidate={invalidate.invalidate}
          />
        </Section>

        <Section n={7} title="Strategic next steps for account" className="lg:col-span-3">
          <Steps
            view={view}
            base={base}
            canWrite={canWrite}
            actorId={actorId}
            tier={tier}
            invalidate={invalidate.invalidate}
          />
        </Section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

function Section({
  n,
  title,
  computed = false,
  className,
  children,
}: {
  n: number;
  title: string;
  computed?: boolean;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section
      aria-labelledby={`strategy-section-${n}`}
      className={cn('min-w-0 rounded-lg border bg-card p-4 shadow-sm', className)}
    >
      <h2
        id={`strategy-section-${n}`}
        className="mb-3 flex items-baseline gap-2 text-xs font-semibold uppercase tracking-wide"
      >
        <span className="text-muted-foreground">{n}</span>
        {title}
        {computed && (
          <span className="ml-auto text-[10px] font-normal normal-case text-muted-foreground">
            from the funnel
          </span>
        )}
      </h2>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mb-2 text-sm">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Prose({ value }: { value: string | null }): React.ReactElement {
  return value ? (
    <p className="whitespace-pre-line">{value}</p>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

function UserName({ user }: { user: StrategyUserRef | null }): React.ReactElement {
  if (user === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span>
      {user.display_name}
      {!user.is_active && (
        <Badge variant="risk" className="ml-1">
          Inactive
        </Badge>
      )}
    </span>
  );
}

/** A referenced contact deleted or redacted since is shown as such — never blank, never an error. */
function ContactName({ contact }: { contact: StrategyContactRef | null }): React.ReactElement {
  if (contact === null) return <span className="text-muted-foreground">—</span>;
  const label = contact.is_redacted ? 'Redacted contact' : contact.name || 'Unnamed contact';
  return (
    <span>
      <span className={cn(contact.is_deleted && 'line-through')}>{label}</span>
      {contact.title && !contact.is_redacted && (
        <span className="text-muted-foreground"> · {contact.title}</span>
      )}
      {contact.is_redacted && (
        <Badge variant="risk" className="ml-1">
          Redacted
        </Badge>
      )}
      {contact.is_deleted && !contact.is_redacted && (
        <Badge variant="risk" className="ml-1">
          Removed
        </Badge>
      )}
      {!contact.is_active && !contact.is_deleted && !contact.is_redacted && (
        <Badge variant="risk" className="ml-1">
          Inactive
        </Badge>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header({ view, canWrite }: { view: View; canWrite: boolean }): React.ReactElement {
  const dash = '—';
  const tiles: { label: string; value: string; note?: string }[] = [
    {
      label: 'Open pipeline',
      value: formatMoney(view.header.openPipeline.value),
      note: `${view.header.openPipeline.count} open · ${view.header.openPipeline.basisLabel}`,
    },
    {
      label: 'Won backlog',
      value: formatMoney(view.header.wonBacklog.value),
      note: `${view.header.wonBacklog.count} won, pre-production · gross · ${view.header.wonBacklog.basisLabel}`,
    },
    { label: 'FY26 revenue', value: dash },
    { label: 'FY27 forecast', value: dash },
    { label: 'FY28 forecast', value: dash },
    { label: 'Growth', value: dash },
  ];
  return (
    <header className="space-y-3 rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Strategic account plan
          </p>
          <h1 className="truncate text-xl font-semibold tracking-tight">
            <Link to={`/accounts/${view.account.id}`} className="hover:underline">
              {view.account.name}
            </Link>
          </h1>
          <p className="text-xs text-muted-foreground">
            Owner: <UserName user={view.account.owner} /> · Updated:{' '}
            {view.header.updatedAt ? formatDateTime(view.header.updatedAt) : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!canWrite && (
            <Badge variant="outline" data-testid="read-only-badge">
              Read only
            </Badge>
          )}
          <ExportPowerPointButton accountId={view.account.id} accountName={view.account.name} />
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-md border p-2">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {tile.label}
            </dt>
            <dd className="text-lg font-semibold tabular-nums">{tile.value}</dd>
            {tile.note && (
              <dd className="text-[10px] leading-tight text-muted-foreground">{tile.note}</dd>
            )}
          </div>
        ))}
      </dl>
      <p className="text-[11px] text-muted-foreground">{view.header.forecastNote}</p>
    </header>
  );
}

/**
 * Export to PowerPoint: one slide, built by the API from the same view this
 * page shows (`docs/account-strategy.md`, Export). Everyone who can see the
 * page may export it. The file comes through the API with the user's own
 * token, as the CSV exports do — never from a public or cached URL.
 */
function ExportPowerPointButton({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName: string;
}): React.ReactElement {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    try {
      const response = await apiRaw(`/api/account-strategies/${accountId}/export.pptx`, {
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.presentationml.presentation, application/json',
        },
      });
      if (!response.ok) {
        let message = 'The slide could not be generated. Try again in a moment.';
        try {
          const body = (await response.json()) as { error?: { message?: string } };
          if (body.error?.message) message = body.error.message;
        } catch {
          // Not JSON: keep the generic message.
        }
        push({ tone: 'error', title: 'Export to PowerPoint failed', body: message });
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileNameOf(response.headers.get('content-disposition'), accountName);
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      push({
        tone: 'error',
        title: 'Export to PowerPoint failed',
        body: err instanceof ApiError ? err.message : 'The server could not be reached.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void run()}
      disabled={busy}
      aria-busy={busy}
      data-testid="export-pptx"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {busy ? 'Generating…' : 'Export to PowerPoint'}
    </Button>
  );
}

/** The server's file name (`filename*` first), else one built the same way. */
function fileNameOf(disposition: string | null, accountName: string): string {
  const star = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (star) {
    try {
      return decodeURIComponent(star);
    } catch {
      // Fall through.
    }
  }
  const plain = disposition?.match(/filename="([^"]+)"/i)?.[1];
  return plain ?? `${accountName} - Account Strategy.pptx`;
}

// ---------------------------------------------------------------------------
// Section 1
// ---------------------------------------------------------------------------

function Relationship({
  view,
  canWrite,
  onSave,
}: {
  view: View;
  canWrite: boolean;
  onSave: (body: Record<string, unknown>) => void;
}): React.ReactElement {
  const reference = useReferenceData();
  const r = view.relationship;

  if (!canWrite) {
    return (
      <>
        <Field label="Business unit">
          <Prose value={r.business_unit} />
        </Field>
        <Field label="Customer exec sponsor">
          <ContactName contact={r.customer_exec_sponsor} />
        </Field>
        <Field label="Menlo exec sponsor">
          <UserName user={r.menlo_exec_sponsor} />
        </Field>
        <Field label="Champion">
          <ContactName contact={r.champion} />
        </Field>
        <Field label="Detractor">
          <ContactName contact={r.detractor} />
        </Field>
      </>
    );
  }

  const activeUsers = reference.users.filter((u) => u.is_active);
  return (
    <>
      <Field label="Business unit">
        <SaveOnBlurText
          label="Business unit"
          value={r.business_unit}
          placeholder="BU / division"
          onSave={(value) => onSave({ business_unit: value })}
        />
      </Field>
      <ContactPicker
        label="Customer exec sponsor"
        current={r.customer_exec_sponsor}
        contacts={view.contacts}
        onChange={(id) => onSave({ customer_exec_sponsor_contact_id: id })}
      />
      <Field label="Menlo exec sponsor">
        <Select
          aria-label="Menlo exec sponsor"
          value={r.menlo_exec_sponsor?.id ?? ''}
          onChange={(event) => onSave({ menlo_exec_sponsor_user_id: event.target.value || null })}
        >
          <option value="">—</option>
          {r.menlo_exec_sponsor && !r.menlo_exec_sponsor.is_active && (
            <option value={r.menlo_exec_sponsor.id}>
              {r.menlo_exec_sponsor.display_name} (inactive)
            </option>
          )}
          {activeUsers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.display_name}
            </option>
          ))}
        </Select>
      </Field>
      <ContactPicker
        label="Champion"
        current={r.champion}
        contacts={view.contacts}
        preferDisposition="champion"
        onChange={(id) => onSave({ champion_contact_id: id })}
      />
      <ContactPicker
        label="Detractor"
        current={r.detractor}
        contacts={view.contacts}
        preferDisposition="detractor"
        onChange={(id) => onSave({ detractor_contact_id: id })}
      />
    </>
  );
}

/**
 * A contact on this account. When `preferDisposition` is given, the contacts
 * already recorded with that disposition are offered first; the explicit
 * choice is what is stored, and `contacts.disposition` is never written back.
 */
function ContactPicker({
  label,
  current,
  contacts,
  preferDisposition,
  onChange,
}: {
  label: string;
  current: StrategyContactRef | null;
  contacts: StrategyContactRef[];
  preferDisposition?: 'champion' | 'detractor';
  onChange: (id: string | null) => void;
}): React.ReactElement {
  const preferred = preferDisposition
    ? contacts.filter((c) => c.disposition === preferDisposition)
    : [];
  const rest = contacts.filter((c) => !preferred.includes(c));
  const currentMissing = current !== null && !contacts.some((c) => c.id === current.id);
  const optionLabel = (c: StrategyContactRef): string =>
    c.title ? `${c.name} · ${c.title}` : c.name;

  return (
    <Field label={label}>
      <Select
        aria-label={label}
        value={current?.id ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">—</option>
        {currentMissing && current && (
          <option value={current.id}>
            {current.is_redacted ? 'Redacted contact' : current.name} (no longer current)
          </option>
        )}
        {preferred.length > 0 && (
          <optgroup label={`Recorded as ${preferDisposition}`}>
            {preferred.map((c) => (
              <option key={c.id} value={c.id}>
                {optionLabel(c)}
              </option>
            ))}
          </optgroup>
        )}
        {preferred.length > 0 ? (
          <optgroup label="Other contacts">
            {rest.map((c) => (
              <option key={c.id} value={c.id}>
                {optionLabel(c)}
              </option>
            ))}
          </optgroup>
        ) : (
          rest.map((c) => (
            <option key={c.id} value={c.id}>
              {optionLabel(c)}
            </option>
          ))
        )}
      </Select>
      {currentMissing && (
        <div className="mt-1 text-xs">
          <ContactName contact={current} />
        </div>
      )}
    </Field>
  );
}

function SaveOnBlurText({
  label,
  value,
  placeholder,
  multiline = false,
  onSave,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  multiline?: boolean;
  onSave: (value: string | null) => void;
}): React.ReactElement {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const commit = (): void => {
    const next = draft.trim() === '' ? null : draft;
    if (next !== (value ?? null)) onSave(next);
  };
  return multiline ? (
    <Textarea
      aria-label={label}
      value={draft}
      placeholder={placeholder}
      className="min-h-[60px]"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
    />
  ) : (
    <Input
      aria-label={label}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
    />
  );
}

// ---------------------------------------------------------------------------
// Sections 3 and 8 — ordered lists
// ---------------------------------------------------------------------------

function OrderedList({
  kind,
  base,
  canWrite,
  invalidate,
  items,
}: {
  kind: 'objectives';
  base: string;
  canWrite: boolean;
  invalidate: readonly (readonly unknown[])[];
  items: { id: string; text: string }[];
}): React.ReactElement {
  const [adding, setAdding] = useState('');
  const add = useApiMutation<Record<string, unknown>, string>(
    (body) => ({ path: `${base}/${kind}`, method: 'POST', body: { body } }),
    { invalidate },
  );
  const edit = useApiMutation<Record<string, unknown>, { id: string; body: string }>(
    ({ id, body }) => ({ path: `${base}/${kind}/${id}`, method: 'PUT', body: { body } }),
    { invalidate },
  );
  const remove = useApiMutation<Record<string, unknown>, string>(
    (id) => ({ path: `${base}/${kind}/${id}`, method: 'DELETE' }),
    { invalidate },
  );
  const reorder = useApiMutation<Record<string, unknown>, string[]>(
    (ids) => ({ path: `${base}/${kind}/order`, method: 'PUT', body: { ids } }),
    { invalidate },
  );

  const move = (index: number, delta: number): void => {
    const ids = items.map((i) => i.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate(ids);
  };

  return (
    <div className="space-y-2 text-sm">
      {items.length === 0 && <p className="text-muted-foreground">No objectives yet.</p>}
      <ul className="space-y-1">
        {items.map((item, index) => (
          <li key={item.id} className="flex items-start gap-1">
            <span aria-hidden="true" className="pt-2">
              •
            </span>
            {canWrite ? (
              <>
                <div className="min-w-0 flex-1">
                  <SaveOnBlurText
                    label={`Objective ${index + 1}`}
                    value={item.text}
                    onSave={(value) => value && edit.mutate({ id: item.id, body: value })}
                  />
                </div>
                <IconButton label="Move up" disabled={index === 0} onClick={() => move(index, -1)}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton
                  label="Move down"
                  disabled={index === items.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton label="Remove" onClick={() => remove.mutate(item.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </>
            ) : (
              <span className="pt-1">{item.text}</span>
            )}
          </li>
        ))}
      </ul>
      {canWrite && (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (adding.trim() === '') return;
            add.mutate(adding.trim(), { onSuccess: () => setAdding('') });
          }}
        >
          <Input
            aria-label="New objective"
            value={adding}
            placeholder="Win [platform / socket] and grow content…"
            onChange={(event) => setAdding(event.target.value)}
          />
          <Button type="submit" size="sm" variant="outline">
            Add
          </Button>
        </form>
      )}
    </div>
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className="h-9 w-8 shrink-0"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function Blockers({
  view,
  base,
  canWrite,
  invalidate,
}: {
  view: View;
  base: string;
  canWrite: boolean;
  invalidate: readonly (readonly unknown[])[];
}): React.ReactElement {
  const [category, setCategory] = useState<string>('technical');
  const [note, setNote] = useState('');
  const add = useApiMutation<Record<string, unknown>, Record<string, unknown>>(
    (body) => ({ path: `${base}/blockers`, method: 'POST', body }),
    { invalidate },
  );
  const edit = useApiMutation<
    Record<string, unknown>,
    { id: string; body: Record<string, unknown> }
  >(({ id, body }) => ({ path: `${base}/blockers/${id}`, method: 'PUT', body }), { invalidate });
  const remove = useApiMutation<Record<string, unknown>, string>(
    (id) => ({ path: `${base}/blockers/${id}`, method: 'DELETE' }),
    { invalidate },
  );
  const reorder = useApiMutation<Record<string, unknown>, string[]>(
    (ids) => ({ path: `${base}/blockers/order`, method: 'PUT', body: { ids } }),
    { invalidate },
  );
  const items = view.blockers;
  const move = (index: number, delta: number): void => {
    const ids = items.map((i) => i.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate(ids);
  };

  return (
    <div className="mb-3 space-y-2 text-sm">
      {items.length === 0 && <p className="text-muted-foreground">No blockers recorded.</p>}
      <ul className="space-y-2">
        {items.map((blocker, index) => (
          <li key={blocker.id} className="rounded-md border p-2">
            {canWrite ? (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Select
                    aria-label={`Blocker ${index + 1} category`}
                    value={blocker.category}
                    onChange={(event) =>
                      edit.mutate({ id: blocker.id, body: { category: event.target.value } })
                    }
                  >
                    {STRATEGY_BLOCKER_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {STRATEGY_BLOCKER_LABELS[c]}
                      </option>
                    ))}
                  </Select>
                  <IconButton
                    label="Move up"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton
                    label="Move down"
                    disabled={index === items.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton label="Remove" onClick={() => remove.mutate(blocker.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
                <SaveOnBlurText
                  label={`Blocker ${index + 1} note`}
                  value={blocker.note}
                  placeholder="Note"
                  onSave={(value) => edit.mutate({ id: blocker.id, body: { note: value } })}
                />
              </div>
            ) : (
              <p>
                <span className="font-medium">
                  Blocker: {STRATEGY_BLOCKER_LABELS[blocker.category]}
                </span>
                {blocker.note && <span className="text-muted-foreground"> — {blocker.note}</span>}
              </p>
            )}
          </li>
        ))}
      </ul>
      {canWrite && (
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate(
              { category, note: note.trim() === '' ? null : note.trim() },
              { onSuccess: () => setNote('') },
            );
          }}
        >
          <Select
            aria-label="New blocker category"
            className="w-auto"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            {STRATEGY_BLOCKER_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {STRATEGY_BLOCKER_LABELS[c]}
              </option>
            ))}
          </Select>
          <Input
            aria-label="New blocker note"
            className="min-w-0 flex-1"
            value={note}
            placeholder="Note"
            onChange={(event) => setNote(event.target.value)}
          />
          <Button type="submit" size="sm" variant="outline">
            Add
          </Button>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections 4 and 5 — computed
// ---------------------------------------------------------------------------

function Projects({ view }: { view: View }): React.ReactElement {
  if (view.projects.length === 0) {
    return (
      <p className="mb-3 text-sm text-muted-foreground">
        No open, on-hold or won opportunities on this account.
      </p>
    );
  }
  return (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="py-1 pr-3 font-medium">Platform / end product</th>
            <th className="py-1 pr-3 font-medium">Use case / socket</th>
            <th className="py-1 font-medium">Opportunity</th>
          </tr>
        </thead>
        {view.projects.map((group) => (
          <tbody key={group.program_id ?? 'none'}>
            {group.program_name && (
              <tr>
                <th colSpan={3} className="pt-2 text-left text-xs font-semibold">
                  {group.program_name}
                </th>
              </tr>
            )}
            {group.rows.map((row) => (
              <tr key={row.opportunity_id} className="border-t">
                <td className="py-1 pr-3">{row.platform ?? '—'}</td>
                <td className="py-1 pr-3">{row.use_case ?? '—'}</td>
                <td className="py-1">
                  <Link to={`/opportunities/${row.opportunity_id}`} className="hover:underline">
                    {row.opp_number ?? row.name}
                  </Link>{' '}
                  <span className="text-xs text-muted-foreground">{humanize(row.status)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

function Content({ view }: { view: View }): React.ReactElement {
  const groups = view.content.groups;
  return (
    <div className="space-y-3 text-sm">
      {groups.length === 0 && <p className="text-muted-foreground">No product lines.</p>}
      {groups.map((group) => (
        <div key={group.opportunity_id} className="rounded-md border p-2">
          <p className="text-xs font-medium">
            <Link to={`/opportunities/${group.opportunity_id}`} className="hover:underline">
              {group.opp_number ?? group.name}
            </Link>
            <span className="text-muted-foreground">
              {' '}
              · GM {group.gross_margin_pct ? `${group.gross_margin_pct}%` : '—'}
            </span>
          </p>
          <div className="overflow-x-auto">
            <table className="mt-1 w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="pr-2 font-medium">Menlo part</th>
                  <th className="pr-2 text-right font-medium">ASP</th>
                  <th className="text-right font-medium">Qty / system</th>
                </tr>
              </thead>
              <tbody>
                {group.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="pr-2">
                      {line.part_number}
                      {line.is_future && (
                        <Badge variant="info" className="ml-1">
                          Future part
                        </Badge>
                      )}
                      {line.line_type === 'design_support' && (
                        <span className="ml-1 text-muted-foreground">(design support)</span>
                      )}
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {line.asp === null ? '—' : formatMoney(line.asp, { exact: true })}
                    </td>
                    <td className="text-right tabular-nums">{line.units_per_board ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-xs">
            Content / system:{' '}
            {group.content_per_system === null ? (
              <span className="text-muted-foreground">— {group.content_per_system_reason}</span>
            ) : (
              <span className="font-medium tabular-nums">
                {formatMoney(group.content_per_system, { exact: true })}
              </span>
            )}
            <span className="text-muted-foreground"> · {humanize(group.price_basis)}</span>
          </p>
        </div>
      ))}
      <p className="text-[11px] text-muted-foreground">{view.content.note}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 7 — strategic next steps
// ---------------------------------------------------------------------------

const HEALTH_VARIANT: Record<StrategicStepHealth, 'success' | 'info' | 'risk' | 'danger'> = {
  done: 'success',
  on_track: 'info',
  at_risk: 'risk',
  late: 'danger',
};

function Steps({
  view,
  base,
  canWrite,
  actorId,
  tier,
  invalidate,
}: {
  view: View;
  base: string;
  canWrite: boolean;
  actorId: string;
  tier: 'User' | 'Manager' | 'Admin';
  invalidate: readonly (readonly unknown[])[];
}): React.ReactElement {
  const reference = useReferenceData();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [owner, setOwner] = useState('');
  const add = useApiMutation<Record<string, unknown>, Record<string, unknown>>(
    (body) => ({ path: `${base}/steps`, method: 'POST', body }),
    { invalidate },
  );
  const update = useApiMutation<
    Record<string, unknown>,
    { id: string; body: Record<string, unknown> }
  >(({ id, body }) => ({ path: `${base}/steps/${id}`, method: 'PUT', body }), { invalidate });
  const remove = useApiMutation<Record<string, unknown>, string>(
    (id) => ({ path: `${base}/steps/${id}`, method: 'DELETE' }),
    { invalidate },
  );

  const ownerOptions = (
    <>
      <option value="">— nobody yet —</option>
      <optgroup label="Menlo">
        {reference.users
          .filter((u) => u.is_active)
          .map((u) => (
            <option key={u.id} value={`user:${u.id}`}>
              {u.display_name}
            </option>
          ))}
      </optgroup>
      {view.contacts.length > 0 && (
        <optgroup label="Customer">
          {view.contacts.map((c) => (
            <option key={c.id} value={`contact:${c.id}`}>
              {c.name}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );

  const ownerBody = (value: string): Record<string, unknown> => {
    if (value.startsWith('contact:')) {
      return { owner_type: 'customer', owner_contact_id: value.slice(8), owner_user_id: null };
    }
    return {
      owner_type: 'internal',
      owner_user_id: value.startsWith('user:') ? value.slice(5) : null,
      owner_contact_id: null,
    };
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-3 font-medium">Date</th>
              <th className="py-1 pr-3 font-medium">Action / milestone</th>
              <th className="py-1 pr-3 font-medium">Owner</th>
              <th className="py-1 pr-3 font-medium">Status</th>
              {canWrite && (
                <th className="py-1 font-medium">
                  <span className="sr-only">Remove</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {view.steps.map((step) => {
              const assignee = canAssigneeUpdateStrategicStep({ userId: actorId, tier }, step, [
                'status',
              ]);
              const canStatus = canWrite || assignee;
              return (
                <tr key={step.id} className="border-t align-top">
                  <td className="py-1 pr-3 whitespace-nowrap">
                    {canWrite ? (
                      <Input
                        type="date"
                        aria-label={`Date for ${step.title}`}
                        className="h-8 w-36 text-xs"
                        value={step.due_date ?? ''}
                        onChange={(event) =>
                          update.mutate({
                            id: step.id,
                            body: { due_date: event.target.value || null },
                          })
                        }
                      />
                    ) : (
                      formatDate(step.due_date)
                    )}
                  </td>
                  <td className="py-1 pr-3">
                    {canWrite ? (
                      <SaveOnBlurText
                        label={`Action ${step.title}`}
                        value={step.title}
                        onSave={(value) =>
                          value && update.mutate({ id: step.id, body: { title: value } })
                        }
                      />
                    ) : (
                      step.title
                    )}
                    {step.completion_note && (
                      <p className="text-xs text-muted-foreground">{step.completion_note}</p>
                    )}
                  </td>
                  <td className="py-1 pr-3">
                    {canWrite ? (
                      <Select
                        aria-label={`Owner for ${step.title}`}
                        className="h-8 text-xs"
                        value={
                          step.owner_user_id
                            ? `user:${step.owner_user_id}`
                            : step.owner_contact_id
                              ? `contact:${step.owner_contact_id}`
                              : ''
                        }
                        onChange={(event) =>
                          update.mutate({ id: step.id, body: ownerBody(event.target.value) })
                        }
                      >
                        {ownerOptions}
                      </Select>
                    ) : step.owner_type === 'customer' ? (
                      <ContactName contact={step.owner_contact} />
                    ) : (
                      <UserName user={step.owner_user} />
                    )}
                  </td>
                  <td className="py-1 pr-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={HEALTH_VARIANT[step.health]}>
                        {STRATEGIC_STEP_HEALTH_LABELS[step.health]}
                      </Badge>
                      {canStatus && (
                        <Select
                          aria-label={`Status for ${step.title}`}
                          className="h-8 w-auto text-xs"
                          value={step.status}
                          onChange={(event) =>
                            update.mutate({ id: step.id, body: { status: event.target.value } })
                          }
                        >
                          {ACTION_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {humanize(s)}
                            </option>
                          ))}
                        </Select>
                      )}
                    </div>
                  </td>
                  {canWrite && (
                    <td className="py-1">
                      <IconButton
                        label={`Remove ${step.title}`}
                        onClick={() => remove.mutate(step.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </td>
                  )}
                </tr>
              );
            })}
            {view.steps.length === 0 && (
              <tr>
                <td colSpan={5} className="py-3 text-center text-muted-foreground">
                  No next steps yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {view.hiddenCancelledSteps > 0 && (
        <p className="text-xs text-muted-foreground">
          {view.hiddenCancelledSteps} cancelled step{view.hiddenCancelledSteps === 1 ? '' : 's'}{' '}
          hidden.
        </p>
      )}
      {canWrite && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (title.trim() === '') return;
            add.mutate(
              { title: title.trim(), due_date: due || null, ...ownerBody(owner) },
              {
                onSuccess: () => {
                  setTitle('');
                  setDue('');
                  setOwner('');
                },
              },
            );
          }}
        >
          <Input
            type="date"
            aria-label="New step date"
            className="w-40"
            value={due}
            onChange={(event) => setDue(event.target.value)}
          />
          <Input
            aria-label="New step action"
            className="min-w-0 flex-1"
            placeholder="Customer test / qual / pricing / PO milestone"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <Select
            aria-label="New step owner"
            className="w-48"
            value={owner}
            onChange={(event) => setOwner(event.target.value)}
          >
            {ownerOptions}
          </Select>
          <Button type="submit" size="sm" variant="outline">
            Add step
          </Button>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two optional images
// ---------------------------------------------------------------------------

interface PresignedPost {
  attachmentId: string;
  url: string;
  fields: Record<string, string>;
}

/**
 * An optional image. With none uploaded the slot says so and nothing else
 * changes. The image is fetched as a short-lived presigned URL from
 * `/api/attachments/:id/inline` — behind the strategy read check — and never
 * cached: a fresh one is asked for each time the slot mounts.
 */
function ImageSlot({
  slot,
  view,
  base,
  canWrite,
  invalidate,
}: {
  slot: StrategyImageSlot;
  view: View;
  base: string;
  canWrite: boolean;
  invalidate: readonly (readonly unknown[])[];
}): React.ReactElement {
  const image = view.images[slot];
  const reference = useReferenceData();
  const { push } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [pending, setPending] = useState<File | null>(null);
  const [attestation, setAttestation] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  const remove = useApiMutation<Record<string, unknown>, void>(
    () => ({ path: `${base}/images/${slot}`, method: 'DELETE' }),
    { invalidate },
  );
  const confirm = useApiMutation<Record<string, unknown>, string>(
    (attachmentId) => ({
      path: `${base}/images/${slot}/confirm`,
      method: 'POST',
      body: { attachmentId },
    }),
    { invalidate },
  );

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    if (image === null) return undefined;
    api<{ url: string }>(`/api/attachments/${image.attachmentId}/inline`)
      .then(({ url }) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [image]);

  const accepted = STRATEGY_IMAGE_CONTENT_TYPES.filter((t) =>
    reference.attachment_content_type_allowlist.includes(t),
  );

  const upload = async (): Promise<void> => {
    if (pending === null || attestation !== false) return;
    const file = pending;
    setPending(null);
    setAttestation(null);
    setBusy(true);
    try {
      const presigned = await api<PresignedPost>(`${base}/images/${slot}/presign`, {
        method: 'POST',
        body: {
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          exportControlledDataWillBeEntered: false,
        },
      });
      const form = new FormData();
      for (const [k, v] of Object.entries(presigned.fields)) form.append(k, v);
      form.append('file', file);
      const response = await fetch(presigned.url, { method: 'POST', body: form });
      if (!response.ok) throw new Error(`S3 refused the upload (${response.status}).`);
      await confirm.mutateAsync(presigned.attachmentId);
    } catch (error) {
      push({
        tone: 'error',
        title:
          error instanceof ApiError || error instanceof Error ? error.message : 'Upload failed.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <figure className="mt-3 rounded-md border border-dashed p-2">
      <figcaption className="mb-1 flex items-center justify-between gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {STRATEGY_IMAGE_LABELS[slot]}
        {canWrite && (
          <span className="flex gap-1 normal-case">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
              {image ? 'Replace' : 'Upload'}
            </Button>
            {image && (
              <Button type="button" size="sm" variant="ghost" onClick={() => remove.mutate()}>
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">Remove {STRATEGY_IMAGE_LABELS[slot]}</span>
              </Button>
            )}
          </span>
        )}
      </figcaption>
      {image === null ? (
        <p className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
          <ImageOff className="h-4 w-4" aria-hidden="true" /> No image.
        </p>
      ) : src === null ? (
        <p className="py-4 text-xs text-muted-foreground">Loading image…</p>
      ) : (
        <img
          src={src}
          alt={STRATEGY_IMAGE_LABELS[slot]}
          className="max-h-64 w-full object-contain"
        />
      )}
      {canWrite && (
        <input
          ref={inputRef}
          type="file"
          accept={accepted.join(',')}
          className="sr-only"
          aria-label={`Choose ${STRATEGY_IMAGE_LABELS[slot]}`}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) setPending(file);
            event.target.value = '';
          }}
        />
      )}
      {pending !== null && (
        <Dialog
          open
          onClose={() => {
            setPending(null);
            setAttestation(null);
          }}
          title={`Upload ${pending.name}`}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button onClick={() => void upload()} disabled={attestation !== false}>
                Upload
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <AttachmentAttestation value={attestation} onChange={setAttestation} />
            {attestation === true && (
              <p className="rounded-md border border-amber-600/40 bg-amber-50 p-3 text-xs text-amber-950">
                Then this image cannot be uploaded here. No credential is issued, so the content
                never reaches storage.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Accepted: {accepted.join(', ') || '—'}.
            </p>
          </div>
        </Dialog>
      )}
    </figure>
  );
}
